import type { ChartUnit } from '../../../../core/types/chart-unit.js';
import { TooltipItem } from '../../../../core/types/tooltip.js';
import { getUnitAsString } from '../../../../core/utils/get-unit-as-string.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import {
  WeakMapDimension,
  DimensionXYPoint,
  CategoricalBarData,
} from '../../../types/categorical-bar-chart-data.js';

export const getClosestCategory = (
  categories: CategoricalBarData[],
  dimensionMap: WeakMapDimension,
  closestPoint?: DimensionXYPoint,
): CategoricalBarData =>
  categories.find((category) =>
    category.dimensions.find(
      (dimension) =>
        safeGet(dimensionMap, dimension).id === closestPoint?.datapoint.id,
    ),
  ) ?? categories[0];

type GetSortedTooltipItems = (
  dimensionMap: WeakMapDimension,
  closestPoint: DimensionXYPoint | undefined,
  grouped: boolean,
  closestCategory: CategoricalBarData,
  formatter: (value: number, unit?: string) => string,
  valueScale: (value: number) => number | undefined,
  unit: ChartUnit,
) => TooltipItem[];

export const getSortedTooltipItems: GetSortedTooltipItems = (
  dimensionMap,
  closestPoint,
  grouped,
  closestCategory,
  formatter,
  valueScale,
  unit,
) => {
  if (!closestPoint) {
    return [];
  }

  if (!grouped) {
    const { value, ...rest } = closestPoint.datapoint;

    return [
      {
        ...rest,
        values: [formatter(value, getUnitAsString(unit))],
      },
    ];
  }

  return closestCategory.dimensions
    .map((dimension) => {
      const { id, color, value, name, cumulativeValue } = safeGet(
        dimensionMap,
        dimension,
      );

      return {
        ...dimension,
        id,
        name,
        color,
        values: [formatter(value, getUnitAsString(unit))],
        position: valueScale((cumulativeValue ?? 0) + value) || 0,
      };
    })
    .sort((item1, item2) => item1.position - item2.position);
};
